import React, { useState, isValidElement, cloneElement, ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { Button, Badge, Typography, Select, message } from 'antd';
import { PauseOutlined, CaretRightOutlined, EditOutlined } from '@ant-design/icons';
import { confirmationModal } from 'vl-common/src/components/Modals';
import HeidiAiTooltip from './HeidiAiTooltip';
import { useHeidiSession } from '@components/Clinicians/Session/hooks/useHeidiSession';
import { useTranslation } from 'react-i18next';
import { useAudioTranscription } from '@hooks/useAudioTranscription';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { MdMicOff } from 'react-icons/md';

interface AIDeclinedReason {
  ai_declined_code: string;
  ai_declined_description: string;
}

const PanelContainer = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 10px 14px 16px;
  width: 90%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const PanelTitle = styled(Typography.Text)`
  font-size: 15px;
  color: var(--productPrimaryDark);
  font-weight: 600;
`;

const StatusText = styled(Typography.Text)`
  font-size: 12px;
  color: #666666;
  font-weight: 500;
`;

const ErrorStatusText = styled(StatusText)`
  color: var(--productRed);
  display: flex;
  gap: 2px;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PauseButton = styled(Button)`
  width: 36px;
  height: 36px;
  background-color: #dcdfe3;
  border-color: #dcdfe3;
  color: #333333;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ReviewButton = styled(Button)`
  flex-grow: 1;
  height: 100%;
  background-color: #de6b40;
  border-color: #de6b40;
  font-weight: 600;
  font-size: 13px;
  border-radius: 6px;
`;

const DescriptionText = styled(Typography.Text)`
  font-size: 12px;
  color: #666666;
  line-height: 1.3;
`;

const ActivateButton = styled(Button)`
  width: 100%;
  height: 42px;
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const SecondaryButton = styled(Button)`
  width: 100%;
  height: auto;
  min-height: 36px;
  padding: 6px 8px;
  white-space: normal;
  text-align: center;
  font-weight: 600;
  font-size: 13px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background-color: #dcdfe3;
  border-color: #dcdfe3;
  color: lightgray;

  > span:not(.anticon) {
    overflow-wrap: anywhere;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const HalfButton = styled(Button)`
  flex: 1;
  height: 42px;
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  background-color: #e5e8eb;
  border-color: #e5e8eb;
  color: lightgray;
`;

const StatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const PoweredByText = styled(Typography.Text)`
  font-size: 10px;
  color: #8c8c8c;
  line-height: 1;
  margin-top: 2px;
`;

const StyledAIImg = styled.img`
  filter: brightness(0) invert(1);
  width: 16px;
  height: 16px;
`;

const CallMenuWrapper = styled.div<{ callText?: string; disabled?: boolean }>`
  width: 100%;

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.65;
      cursor: not-allowed;
    `}

  && .ant-btn {
    width: 100%;
    height: 42px;
    font-weight: 600;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0 !important;

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed !important;
      `}

    * {
      font-size: 0 !important;
    }

    > * {
      display: contents !important;
    }

    &::before {
      content: '';
      order: 1;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      background-image: url('/static/svg/ai-button.svg');
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      filter: brightness(0) invert(1);
    }

    &::after {
      content: '${({ callText }) => callText || 'Start Call'}';
      order: 2;
      font-size: 14px !important;
      font-weight: 600 !important;
      color: ${({ theme }) => theme?.productPrimaryDark || 'var(--productPrimaryDark)'} !important;
      line-height: 1;
    }

    .anticon,
    .anticon *,
    .ant-btn-icon,
    .ant-btn-icon * {
      font-size: 12px !important;
    }

    .anticon,
    .ant-btn-icon {
      order: 3;
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      color: ${({ theme }) => theme?.productPrimaryDark || 'var(--productPrimaryDark)'} !important;

      svg {
        display: block !important;
        width: 12px !important;
        height: 12px !important;
        fill: currentColor !important;
      }
    }
  }
`;

interface AITranscribeControlPanelProps {
  isCaseReview: boolean;
  isRinging?: boolean;
  children?: React.ReactNode;
}

export const AITranscribeControlPanel = ({ isCaseReview, isRinging, children }: AITranscribeControlPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { status, startSession, pause, resume, stop, hasTranscriptionError } = useHeidiSession();
  const { isAudioError } = useAudioTranscription();
  const { data: appointmentsMeta } = useAppointmentMeta();
  const { appointment: { appointment_id, clinician_user_guid } = {} } = useSessionAppointment() ?? {};
  const [showOptOutView, setShowOptOutView] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (hasTranscriptionError) return null;

  const handleContinueOptOut = async () => {
    if (!selectedReason || !appointment_id || !clinician_user_guid) return;

    try {
      setIsSubmitting(true);
      await dispatch(actions.postAIDeclined(clinician_user_guid, appointment_id, selectedReason));
      await queryClient.invalidateQueries();
    } catch {
      message.error(t('Failed to update AI status. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReviewClick = () => {
    confirmationModal({
      title: t('Confirm Review Mode'),
      content: t('Are you sure? You cannot turn AI back on after you go into review mode.'),
      okText: t('Confirm'),
      cancelText: t('Cancel'),
      onOk: stop
    });
  };

  const handlePauseClick = () => (status === 'active' ? pause() : resume());

  const statusMap: Record<string, { text: string; showBadge?: boolean }> = {
    idle: { text: t('Ready') },
    active: { text: t('Transcribing'), showBadge: true },
    paused: { text: t('Paused') },
    processing: { text: t('Generating Notes') },
    completed: { text: t('Review Mode') }
  };

  const showControls = (status === 'paused' || status === 'active') && !isAudioError;

  const metaObj = Array.isArray(appointmentsMeta) ? appointmentsMeta[0] : appointmentsMeta;
  const aiDeclinedReasons: AIDeclinedReason[] = metaObj?.ai_declined_reasons ?? [];

  const reasonOptions = aiDeclinedReasons.map((reason: AIDeclinedReason) => ({
    value: reason.ai_declined_code,
    label: t(reason.ai_declined_description?.trim() ?? '')
  }));

  if (showOptOutView) {
    return (
      <PanelContainer>
        <PanelTitle>{t('Continuing without AI')}</PanelTitle>
        <DescriptionText>{t("Please let us know why you're not using AI today.")}</DescriptionText>

        <Select
          placeholder={t('Select a reason...')}
          data-testid="ai-disabled-reason-dropdown"
          value={selectedReason}
          onChange={setSelectedReason}
          options={reasonOptions}
          disabled={isSubmitting}
        />

        <ButtonRow data-testid="ai-disabled-button-row">
          <HalfButton
            className="heidi-half-btn"
            data-testid="ai-disabled-back-button"
            disabled={isSubmitting}
            onClick={() => setShowOptOutView(false)}
          >
            {t('Back')}
          </HalfButton>

          <HalfButton
            className="heidi-half-btn"
            data-testid="ai-disabled-continue-button"
            disabled={!selectedReason || isSubmitting}
            loading={isSubmitting}
            onClick={handleContinueOptOut}
          >
            {t('Continue')}
          </HalfButton>
        </ButtonRow>
      </PanelContainer>
    );
  }

  return (
    <PanelContainer>
      <HeaderRow>
        <HeaderSection>
          <HeidiAiTooltip />
          <PanelTitle>
            {status === 'idle' && isCaseReview ? t('Activate AI Transcription') : t('AI Assistant')}
          </PanelTitle>
        </HeaderSection>

        <StatusWrapper>
          <HeaderSection>
            {statusMap[status]?.showBadge && <Badge color="#f0625d" style={{ marginBottom: 1 }} />}
            <StatusText>{statusMap[status]?.text ?? t('Ready')}</StatusText>
          </HeaderSection>

          <PoweredByText>{t('Powered by Heidi')}</PoweredByText>
        </StatusWrapper>
      </HeaderRow>

      {status === 'idle' && !isCaseReview && children && (
        <>
          <DescriptionText>
            {t('Start the call and AI will begin to transcribe your consultation automatically')}
          </DescriptionText>
          <CallMenuWrapper callText={t('Start Call')}>
            {isValidElement(children)
              ? cloneElement(children as ReactElement<{ disabled?: boolean }>, { disabled: isRinging })
              : children}
          </CallMenuWrapper>
        </>
      )}

      {status === 'completed' && (
        <DescriptionText>{t('AI transcription is unavailable in Review Mode')}</DescriptionText>
      )}

      {status === 'idle' && (
        <>
          {isCaseReview && (
            <>
              <DescriptionText>{t('Activate AI to begin transcribing your review')}</DescriptionText>
              <ActivateButton data-testid="start-transcript-button" type="primary" onClick={startSession}>
                <StyledAIImg src="/static/svg/ai-button.svg" alt={t('AI Assistant')} />
                {t('Activate AI Transcription')}
              </ActivateButton>
            </>
          )}

          <SecondaryButton
            className="heidi-secondary-btn"
            data-testid="ai-opt-out-button"
            onClick={() => setShowOptOutView(true)}
          >
            <EditOutlined style={{ fontSize: 16 }} />
            {t('Problems with AI? Type notes manually')}
          </SecondaryButton>
        </>
      )}

      {showControls && (
        <ControlsRow>
          <PauseButton
            data-testid="play-pause-transcript-button"
            icon={
              status === 'active' ? (
                <PauseOutlined style={{ fontSize: 14 }} />
              ) : (
                <CaretRightOutlined style={{ fontSize: 14 }} />
              )
            }
            onClick={handlePauseClick}
          />
          <ReviewButton data-testid="review-transcript-button" type="primary" onClick={handleReviewClick}>
            {t('Review Mode')}
          </ReviewButton>
        </ControlsRow>
      )}

      {isAudioError && (
        <ErrorStatusText>
          <MdMicOff aria-hidden="true" fontSize={20} />
          {t('Microphone Error')}
        </ErrorStatusText>
      )}
    </PanelContainer>
  );
};
